<template>
  <AsidePanel
    ref="asidePanel"
    :components="components"
    :eventBus="eventBus"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :redirectTo="redirectTo"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="commands"
      />
    </template>
    <template
      v-if="showSaveAndCreateTemplateButton"
      v-slot:additionalActions
    >
      <ButtonSecondary
        :class="$style.space"
        :outline="false"
        text="save and create template"
        textCase="lowercase"
        @click="handleSaveAndCreateTemplate"
      />
    </template>
  </AsidePanel>
</template>

<script>
import {
  ButtonSecondary,
} from '@jumpcloud/ui-components';
import {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
  osFamilyTypes,
} from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'components/AsidePanel/mixins/AsidePanelPropsMixin';
import CommandDetails from 'client/Commands/CommandDetails/CommandDetails.vue';
import CommandRunnerItemTable from 'client/Commands/CommandRunnerItemTable.vue';
import MspState from 'client/components/models/MspState';
import SaveReturnValue from 'components/AsidePanel/SaveReturnValue';
import SystemGroupItemTable from 'components/ItemTable/SystemGroupItemTable.vue';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';
import Vue from 'vue';

const { create, update } = ItemActionConsts;
const commandModel = StoreModelMap[ItemTypeConsts.command];
const commandCollection = StoreModuleMap[ItemTypeConsts.command];
const commandRunnerCollection = StoreModuleMap[ItemTypeConsts.commandRunner];

export default {
  name: 'CommandAsidePanel',
  components: {
    AsidePanel,
    AsideIcon,
    ButtonSecondary,
  },

  mixins: [
    AsidePanelPropsMixin,
  ],

  props: {
    isRegistrationTrigger: Boolean,
  },

  data() {
    return {
      itemHasBeenSaved: false,
      shouldCreateTemplate: false,
      redirectTo: ItemTypeRoutes[ItemTypeConsts.command],
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      eventBus: new Vue(),
    };
  },

  computed: {
    ...mapGetters(commandRunnerCollection, {
      usersToBind: 'getItemsToBind',
    }),
    ...mapGetters('SettingsModule', [
      'isAdminWithBilling',
      'isAdmin',
    ]),
    ...mapGetters(commandModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'isItemBeingCreated',
      'item',
    ]),

    components() {
      const components = [
        {
          label: 'Details',
          vueComponent: CommandDetails,
          vueComponentProps: {
            isRegistrationTrigger: this.isRegistrationTrigger,
          },
        },
        {
          label: 'Device Groups',
          backboneView: null,
          headerLabels: {
            [create]: 'This command is to be run on the following device groups',
            [update]: 'is run on the following device groups',
          },
          itemType: ItemTypeConsts.deviceGroup,
          vueComponent: SystemGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/commands/${this.itemId}/systemgroups` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.commandAsidePanel,
          },
        },
        {
          label: 'Devices',
          backboneView: null,
          itemType: ItemTypeConsts.device,
          headerLabels: {
            [create]: 'This command is to be run on the following devices',
            [update]: 'is run on the following devices',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/commands/${this.itemId}/systems` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.commandAsidePanel,
            parentOS: this.getSelectedOS(this.item.commandType),
          },
        },
        {
          label: 'Command Runners',
          backboneView: null,
          headerLabels: {
            [create]: 'This command is available to the following command runners',
            [update]: 'is available to the following command runners',
          },
          itemType: ItemTypeConsts.commandRunner,
          vueComponent: CommandRunnerItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/commands/${this.itemId}` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.commandAsidePanel,
          },
        },
      ];
      return this.isRegistrationTrigger ? [components[0]] : components;
    },

    showSaveAndCreateTemplateButton() {
      return !!this.$hasFeatureFlag(this.$featureFlags.commandTemplateManage)
      && MspState.isAnMsp
      && (this.isAdminWithBilling || this.isAdmin);
    },
  },

  methods: {
    ...mapActions(commandModel, [
      'bindCommandToSystems',
      'bindCommandToSystemGroups',
      'saveItem',
      'setItemField',
      'saveAndCreateTemplate',
    ]),

    ...mapActions(commandCollection, [
      'refreshCollection',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    getSelectedOS(commandType) {
      if (commandType === 'mac') return osFamilyTypes.darwin;
      return osFamilyTypes[commandType];
    },

    async handleSave() {
      await this.$validator.validate();
      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      this.itemHasBeenSaved = true;

      // Include command runners before saving
      const commandRunners = this.usersToBind.map(user => user.id);
      this.setItemField({ key: 'commandRunners', value: commandRunners });

      await (this.shouldCreateTemplate
        ? this.validateTemplateCreation() : this.saveItem());
      this.shouldCreateTemplate = false;
      await Promise.allSettled([
        this.bindCommandToSystems(),
        this.bindCommandToSystemGroups(),
      ]);
      await this.refreshCollection();

      return new SaveReturnValue();
    },

    handleSaveAndCreateTemplate() {
      this.shouldCreateTemplate = true;
      this.eventBus.$emit('mainActionSaveButtonClick');
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) return false;
      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    async validateTemplateCreation() {
      try {
        await this.saveAndCreateTemplate();
        this.triggerSuccessNotification({
          message: 'Template saved successfully. Any sensitive information from the original command, such as files, won\'t be saved to the new template.',
          title: '',
        });
      } catch {
        this.triggerErrorNotification({
          message: 'Failed to Save the Command Template ',
          title: 'Failed to Save',
          showCloseButton: true,
        });
      }
    },
  },
};
</script>
<style module scoped>
.space {
  line-height: 0.8;
  margin-right: var(--jc-spacer-medium);
}
</style>
